/*
 * Project: bellcloud
 * File: components/ui/common/AlarmSummary.tsx
 * Description: Alarm summary component displaying current and historical alarm data
 */

import React from 'react';
import styled from '@emotion/styled';
import DashboardAlarmIcon from '/icons/dashboardAlarm.svg';
import DashboardAlertIcon from '/icons/dashboardAlert.svg';

interface AlarmSummaryData {
  current: number;
  in10Min: number;
}

interface AlarmSummaryProps {
  title: string;
  data: AlarmSummaryData;
  variant?: 'danger' | 'caution';
  className?: string;
  onClick?: () => void;
}

const Container = styled.div<{ clickable?: boolean }>`
  display: flex;
  padding: ${({ theme }) => theme.spacing[7]}px;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[10]}px;
  flex: 1 0 0;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
  ${({ clickable, theme }) =>
    clickable &&
    `&:hover {
      border-color: ${theme.colors.border.interactive.hover};
      background-color: ${theme.colors.surface.interactive.hover};
    }`}
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.lg}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 130%;
  margin: 0;
`;

const AlarmSummaryGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
`;

const SummaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]}px;
`;

const BlockLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 130%;
`;

const BlockValueGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]}px;
`;

const ValueContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
`;

const ValueText = styled.span<{ variant?: 'danger' | 'caution' | 'default' }>`
  color: ${({ theme, variant = 'default' }) => {
    switch (variant) {
      case 'danger':
        return theme.colors.status.danger.stroke.text;
      case 'caution':
        return theme.colors.status.caution.stroke.text;
      default:
        return theme.colors.text.primary;
    }
  }};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.xl}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 130%;
`;

const IconImage = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const DifferenceChip = styled.div<{ isPositive: boolean }>`
  display: flex;
  padding: ${({ theme }) => theme.spacing[1]}px ${({ theme }) => theme.spacing[4]}px;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  border: 1px solid ${({ theme }) => theme.colors.icon.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 130%;
`;

const HistoricalValue = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.lg}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 130%;
`;

export const AlarmSummary: React.FC<AlarmSummaryProps> = ({
  title,
  data,
  variant = 'danger',
  className,
  onClick,
}) => {
  const difference = data.current - data.in10Min;
  const showDifference = difference !== 0;
  const isPositive = difference > 0;

  const iconSrc = variant === 'danger' ? DashboardAlarmIcon : DashboardAlertIcon;

  return (
    <Container className={className} clickable={!!onClick} onClick={onClick}>
      <Title>{title}</Title>
      <AlarmSummaryGroup>
        {/* Current Block */}
        <SummaryBlock>
          <BlockLabel>Current</BlockLabel>
          <BlockValueGroup>
            <ValueContainer>
              {data.current > 0 && <IconImage src={iconSrc} alt={`${title} icon`} />}
              <ValueText variant={data.current > 0 ? variant : 'default'}>{data.current}</ValueText>
            </ValueContainer>
            {showDifference && (
              <DifferenceChip isPositive={isPositive}>
                {isPositive ? '+' : ''}
                {difference}
              </DifferenceChip>
            )}
          </BlockValueGroup>
        </SummaryBlock>

        {/* 10 mins before Block */}
        <SummaryBlock>
          <BlockLabel>10 mins Ago</BlockLabel>
          <HistoricalValue>{data.in10Min}</HistoricalValue>
        </SummaryBlock>
      </AlarmSummaryGroup>
    </Container>
  );
};
